import React, { useEffect, useState } from "react";
import FicheHeader from "./FicheHeader";
import { api, formatEURDecimal, formatDateFR, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { confirmAction } from "../ConfirmDialog";
import { Loader2, Printer, CheckCircle2, XCircle, RotateCcw, ReceiptText, X } from "lucide-react";

export const INVOICE_STATUS = {
  issued: { label: "Émise", cls: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  paid: { label: "Payée", cls: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  cancelled: { label: "Annulée", cls: "bg-red-100 text-red-700", dot: "bg-red-500" },
};

export default function InvoiceFiche({ invoiceId, onBack }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [creditForm, setCreditForm] = useState(null); // { amount, reason } | null

  useEffect(() => {
    setLoading(true);
    api.get(`/vendor/invoices/${invoiceId}`)
      .then(({ data }) => setInvoice(data.invoice))
      .catch(() => setInvoice(null))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  const changeStatus = async (status) => {
    const MSG = {
      paid: { title: "Marquer cette facture comme payée ?", message: `La facture ${invoice?.reference || ""} sera enregistrée comme réglée. Le suivi financier sera mis à jour.`, label: "Confirmer : facture payée", variant: "default" },
      cancelled: { title: "Annuler cette facture ?", message: `La facture ${invoice?.reference || ""} sera annulée : elle ne comptera plus dans vos revenus et ne pourra plus être encaissée.`, label: "Confirmer l'annulation", variant: "danger" },
      issued: { title: "Repasser la facture en émise ?", message: `La facture ${invoice?.reference || ""} ne sera plus considérée comme payée.`, label: "Confirmer", variant: "warning" },
    };
    const m = MSG[status];
    if (m && !(await confirmAction({ title: m.title, message: m.message, confirmLabel: m.label, variant: m.variant }))) return;
    setActing(true);
    try {
      const { data } = await api.patch(`/vendor/invoices/${invoiceId}/status`, { status });
      setInvoice(data.invoice);
      toast.success(status === "paid" ? "Facture marquée payée" : status === "cancelled" ? "Facture annulée" : "Facture rétablie");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
    setActing(false);
  };

  const submitCreditNote = async (e) => {
    e.preventDefault();
    if (!creditForm) return;
    if (!(await confirmAction({
      title: "Créer cet avoir ?",
      message: `Un avoir de ${parseFloat(creditForm.amount || 0).toFixed(2)} € sera émis sur la facture ${invoice?.reference || ""}. Un avoir validé est DÉFINITIF et non modifiable.`,
      confirmLabel: "Confirmer la création de l'avoir",
      variant: "warning",
    }))) return;
    setActing(true);
    try {
      const { data } = await api.post(`/vendor/invoices/${invoiceId}/credit-note`, {
        amount: parseFloat(creditForm.amount),
        reason: creditForm.reason,
      });
      setInvoice(data.invoice);
      setCreditForm(null);
      toast.success(`Avoir ${data.credit_note.reference} créé`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de la création de l'avoir");
    }
    setActing(false);
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  if (!invoice) return <div className="py-16 text-center text-sm text-slate-500">Facture introuvable.</div>;

  const meta = INVOICE_STATUS[invoice.status] || INVOICE_STATUS.issued;
  const co = invoice.company || {};
  const fin = invoice.finance || {};
  const isFree = invoice.kind === "free" || !invoice.booking_id;
  const credited = Number(invoice.credited_total || 0);
  const canCredit = invoice.status !== "cancelled" && credited < Number(invoice.total_ttc || 0);
  const creditNotes = invoice.credit_notes || [];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="invoice-fiche">
      <FicheHeader
        onBack={onBack}
        backLabel={isFree ? "Retour à la facturation" : "Retour à la réservation"}
        title={`Facture ${invoice.reference}`}
        subtitle={isFree ? `Facture libre · Émise le ${formatDateFR(invoice.issue_date)}` : `Réservation ${invoice.booking_reference || ""} · Émise le ${formatDateFR(invoice.issue_date)}`}
        badge={
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`} data-testid="invoice-status-badge">
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} /> {meta.label}
          </span>
        }
        actions={
          <>
            {invoice.status === "issued" && (
              <>
                <button onClick={() => changeStatus("paid")} disabled={acting}
                  className="h-10 px-4 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="invoice-mark-paid-btn">
                  <CheckCircle2 className="h-4 w-4" /> Marquer payée
                </button>
                <button onClick={() => changeStatus("cancelled")} disabled={acting}
                  className="h-10 px-4 rounded-xl border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="invoice-cancel-btn">
                  <XCircle className="h-4 w-4" /> Annuler
                </button>
              </>
            )}
            {invoice.status === "paid" && (
              <>
                <button onClick={() => changeStatus("issued")} disabled={acting}
                  className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="invoice-reissue-btn">
                  <RotateCcw className="h-3.5 w-3.5" /> Repasser en émise
                </button>
                <button onClick={() => changeStatus("cancelled")} disabled={acting}
                  className="h-10 px-4 rounded-xl border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="invoice-cancel-btn">
                  <XCircle className="h-4 w-4" /> Annuler
                </button>
              </>
            )}
            {canCredit && (
              <button onClick={() => setCreditForm(creditForm ? null : { amount: "", reason: "" })}
                className="h-10 px-4 rounded-xl border border-violet-200 text-sm font-medium text-violet-700 hover:bg-violet-50 transition-colors inline-flex items-center gap-2"
                data-testid="invoice-credit-note-btn">
                <ReceiptText className="h-3.5 w-3.5" /> Créer un avoir
              </button>
            )}
            <button onClick={() => window.print()}
              className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors inline-flex items-center gap-2"
              data-testid="invoice-print-btn">
              <Printer className="h-3.5 w-3.5" /> Imprimer
            </button>
          </>
        }
        testid="invoice-fiche-header"
      />

      {/* ─── Formulaire avoir ─── */}
      {creditForm && (
        <form onSubmit={submitCreditNote} className="mt-4 rounded-2xl border border-violet-200 bg-violet-50/40 p-4 flex flex-wrap items-end gap-3" data-testid="credit-note-form">
          <div>
            <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">Montant TTC (€) *</label>
            <input type="number" min="0.01" step="0.01" max={(invoice.total_ttc || 0) - credited} required value={creditForm.amount}
              onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
              className="mt-1 w-36 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none" data-testid="credit-note-amount" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">Motif *</label>
            <input required value={creditForm.reason} onChange={(e) => setCreditForm({ ...creditForm, reason: e.target.value })}
              placeholder="Geste commercial, erreur de facturation…"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none" data-testid="credit-note-reason" />
          </div>
          <div className="text-[11px] text-slate-500 w-full sm:w-auto">Max : {formatEURDecimal((invoice.total_ttc || 0) - credited)} · Un avoir validé est définitif et non modifiable.</div>
          <button type="submit" disabled={acting} className="h-[38px] px-5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-2" data-testid="credit-note-submit">
            {acting && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Valider l'avoir
          </button>
          <button type="button" onClick={() => setCreditForm(null)} className="h-[38px] px-3 rounded-xl border border-slate-200 text-xs text-slate-500 hover:bg-white">
            <X className="h-3.5 w-3.5" />
          </button>
        </form>
      )}

      {/* ─── Corps imprimable ─── */}
      <div id="contract-print-area" className="relative mt-6 bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 space-y-8" data-testid="invoice-body">
        {invoice.status === "cancelled" && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-6xl font-bold text-red-200 rotate-[-20deg] tracking-widest uppercase">Annulée</span>
          </div>
        )}

        {/* En-tête société / facture */}
        <div className="flex flex-col sm:flex-row justify-between gap-6">
          <div className="min-w-0">
            {co.logo_data_url ? (
              <img src={co.logo_data_url} alt="Logo" className="h-12 mb-3 object-contain" />
            ) : (
              <div className="font-heading text-xl font-medium text-slate-900">{co.name || "—"}</div>
            )}
            <div className="text-xs text-slate-500 space-y-0.5 mt-1">
              {co.logo_data_url && co.name && <div className="text-sm font-semibold text-slate-800">{co.name}</div>}
              {co.legal_form && <div>{co.legal_form}</div>}
              {co.address && <div className="whitespace-pre-line">{co.address}</div>}
              {co.registration_number && <div>SIREN/SIRET : {co.registration_number}</div>}
              {co.vat_number && <div>TVA : {co.vat_number}</div>}
              {co.phone && <div>{co.phone}</div>}
              {co.email && <div>{co.email}</div>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="overline">Facture</div>
            <div className="font-heading text-2xl font-medium text-slate-900" data-testid="invoice-reference">{invoice.reference}</div>
            <div className="text-xs text-slate-500 mt-2 space-y-0.5">
              <div>Date d'émission : {formatDateFR(invoice.issue_date)}</div>
              <div>Date d'échéance : {formatDateFR(invoice.due_date)}</div>
              {invoice.booking_reference && <div>Réf. réservation : {invoice.booking_reference}</div>}
            </div>
          </div>
        </div>

        {/* Client */}
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
          <div className="overline mb-1.5">Facturé à</div>
          <div className="text-sm font-semibold text-slate-900">{invoice.client?.name}</div>
          <div className="text-xs text-slate-500">{invoice.client?.email}{invoice.client?.phone ? ` · ${invoice.client.phone}` : ""}</div>
        </div>

        {/* Prestation */}
        <div>
          <div className="overline mb-3">{isFree ? "Détail" : "Détail de la prestation"}</div>
          {invoice.vehicle_name && (
            <div className="text-xs text-slate-500 mb-3">
              Véhicule : <span className="font-medium text-slate-700">{invoice.vehicle_name}</span> · Du {formatDateFR(invoice.start_date)} au {formatDateFR(invoice.end_date)}
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="py-2 font-semibold text-slate-600 text-xs uppercase tracking-wider">Désignation</th>
                <th className="py-2 font-semibold text-slate-600 text-xs uppercase tracking-wider text-right">Montant TTC</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.lines || []).map((l, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid={`invoice-line-${i}`}>
                  <td className="py-2.5 text-slate-700">{l.label}</td>
                  <td className={`py-2.5 text-right font-medium ${l.amount < 0 ? "text-emerald-600" : "text-slate-900"}`}>
                    {l.amount < 0 ? `− ${formatEURDecimal(Math.abs(l.amount))}` : formatEURDecimal(l.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end mt-4">
            <div className="w-full sm:w-72 space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-600"><span>Total HT</span><span>{formatEURDecimal(invoice.total_ht)}</span></div>
              <div className="flex justify-between text-slate-600"><span>TVA ({invoice.vat_rate}%)</span><span>{formatEURDecimal(invoice.vat_amount)}</span></div>
              <div className="flex justify-between font-semibold text-slate-900 border-t border-slate-200 pt-1.5"><span>Total TTC</span><span data-testid="invoice-total-ttc">{formatEURDecimal(invoice.total_ttc)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Déjà réglé</span><span>{formatEURDecimal(invoice.amount_paid_at_issue)}</span></div>
              {credited > 0 && (
                <div className="flex justify-between text-emerald-600"><span>Avoirs émis</span><span data-testid="invoice-credited-total">− {formatEURDecimal(credited)}</span></div>
              )}
              <div className={`flex justify-between font-semibold ${invoice.balance_due > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                <span>Reste à payer</span><span data-testid="invoice-balance-due">{formatEURDecimal(invoice.balance_due)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Avoirs liés */}
        {creditNotes.length > 0 && (
          <div data-testid="invoice-credit-notes">
            <div className="overline mb-3">Avoirs liés ({creditNotes.length})</div>
            <div className="space-y-1.5">
              {creditNotes.map((cn, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-xl border border-emerald-100 bg-emerald-50/50 text-sm" data-testid={`invoice-credit-note-${cn.reference}`}>
                  <span className="font-semibold text-emerald-800">{cn.reference}</span>
                  <span className="text-xs text-slate-500">{formatDateFR(cn.issue_date)}</span>
                  <span className="text-xs text-slate-600 flex-1 min-w-0 truncate">{cn.reason}</span>
                  <span className="font-semibold text-emerald-700">− {formatEURDecimal(cn.amount_ttc)}</span>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-slate-400 mt-2">Les avoirs sont définitifs : ils ne peuvent être ni modifiés ni supprimés.</div>
          </div>
        )}

        {/* Règlement */}
        {(fin.iban || fin.bic || fin.bank_name) && (
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
            <div className="overline mb-1.5">Règlement par virement</div>
            <div className="text-xs text-slate-600 space-y-0.5">
              {fin.bank_name && <div>Banque : {fin.bank_name}</div>}
              {fin.iban && <div>IBAN : <span className="font-mono">{fin.iban}</span></div>}
              {fin.bic && <div>BIC : <span className="font-mono">{fin.bic}</span></div>}
              <div>Échéance : {formatDateFR(invoice.due_date)} ({fin.payment_terms_days} jours)</div>
            </div>
          </div>
        )}

        {/* Mentions */}
        <div className="text-[10px] text-slate-400 space-y-1.5 border-t border-slate-100 pt-4">
          {fin.late_penalty_text && <p>{fin.late_penalty_text}</p>}
          {invoice.legal_mention && <p>{invoice.legal_mention}</p>}
          {fin.invoice_footer && <p className="text-slate-500">{fin.invoice_footer}</p>}
        </div>
      </div>
    </div>
  );
}
